import { useEffect } from 'react';
import { syncTrailIfStale } from '../utils/liveTrailUpdates';

// How often to ask whether the open trail has been republished, on top of
// the visibility/online checks below. Five minutes is well inside a typical
// stop-to-stop walk, so an author fixing a wrong clue mid-session reaches
// players before most of them reach the next stop, while costing a phone in
// a pocket twelve tiny requests an hour. syncTrailIfStale's own
// MIN_CHECK_GAP_MS stops this stacking with the event-driven checks.
export const LIVE_TRAIL_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Keeps the open trail in step with what the author has published, for as
 * long as the player has a story open. Checks when the tab comes back to the
 * foreground (the common case: phone pocketed between stops), when the
 * device regains a connection, and on a slow timer for a screen left open.
 *
 * Every check is fire-and-forget: syncTrailIfStale never throws and leaves
 * the on-screen trail alone unless it has genuinely moved on.
 */
export function useLiveTrailUpdates(storyCode: string | undefined): void {
  useEffect(() => {
    if (!storyCode) return;

    const check = () => {
      void syncTrailIfStale(storyCode);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    const intervalId = setInterval(check, LIVE_TRAIL_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', check);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', check);
    };
  }, [storyCode]);
}

export default useLiveTrailUpdates;
